import type { IHubRepository } from "@/application/ports/repositories";
import type {
  HubDashboardSummary,
  HubInvestment,
  HubInvestmentListParams,
  HubInvestmentListResponse,
  HubInvestorProfile,
  HubPendingActions,
  HubPendingActionsParams,
  HubProfilesResponse,
  HubProfileUpdateRequest,
  HubSigningUrlRequest,
  HubSigningUrlResponse,
} from "@/domain/hub";
import type {
  InvestmentDocumentResponse,
  DocumentDownloadResponse,
  DocumentWithReviewResponse,
  PaginatedResponse,
} from "@/domain/documentBlocks";
import { InvestmentState } from "@/domain/investment/types";
import { apiClient, ApiClientError } from "../client";
import { getLogger, getHubProfileStorageService } from "@/infrastructure/di/services";

export class HubRepository implements IHubRepository {
  private basePath = "/hub/investor";
  private getSelectedProfileUuid(): string | undefined {
    try {
      return getHubProfileStorageService().getSelectedProfileUuid() ?? undefined;
    } catch {
      return undefined;
    }
  }

  private handleError(error: unknown, operation: string): never {
    getLogger().error(`Hub ${operation} failed:`, error);
    if (error instanceof ApiClientError) throw error;
    if (error instanceof Error) {
      throw new ApiClientError({
        message: error.message || `Failed to ${operation}`,
        status: 0,
        code: "NETWORK_ERROR",
        details: { originalError: error.message },
      });
    }
    throw new ApiClientError({
      message: `An unexpected error occurred during ${operation}`,
      status: 0,
      code: "UNKNOWN_ERROR",
      details: { error: String(error) },
    });
  }

  private validateResponse<T>(data: unknown, requiredKeys: string[]): T {
    if (!data || typeof data !== "object") {
      throw new ApiClientError({
        message: "Invalid response format",
        status: 500,
        code: "INVALID_RESPONSE",
        details: { received: data },
      });
    }
    const missingKeys = requiredKeys.filter((key) => !(key in (data as object)));
    if (missingKeys.length > 0) {
      throw new ApiClientError({
        message: "Response missing required fields",
        status: 500,
        code: "INCOMPLETE_RESPONSE",
        details: { missingKeys, received: data },
      });
    }
    return data as T;
  }

  private buildUrlWithInvestor(baseUrl: string, investorUuid?: string): string {
    if (!investorUuid) return baseUrl;
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}investor_uuid=${investorUuid}`;
  }

  private convertStateToEnum(state: string): InvestmentState {
    return state as InvestmentState;
  }

  private computeLegalRepName(firstName?: string | null, lastName?: string | null): string | null {
    if (!firstName && !lastName) return null;
    return [firstName, lastName].filter(Boolean).join(" ");
  }

  private transformHubInvestment(
    investment: Omit<HubInvestment, "state"> & { state: string },
  ): HubInvestment {
    return {
      ...investment,
      state: this.convertStateToEnum(investment.state),
    };
  }

  async getProfiles(): Promise<HubProfilesResponse> {
    try {
      const response = await apiClient.get<HubProfilesResponse>(`${this.basePath}/profiles`);
      return this.validateResponse<HubProfilesResponse>(response.data, ["profiles", "count"]);
    } catch (error) {
      throw this.handleError(error, "fetch investor profiles");
    }
  }

  async getProfile(investorUuid?: string): Promise<HubInvestorProfile> {
    try {
      const profileUuid = investorUuid ?? this.getSelectedProfileUuid();
      const url = this.buildUrlWithInvestor(`${this.basePath}/profile`, profileUuid);
      const response = await apiClient.get<HubInvestorProfile>(url);

      const validated = this.validateResponse<HubInvestorProfile>(response.data, [
        "uuid",
        "type",
        "display_name",
      ]);

      return {
        ...validated,
        legal_rep_name: this.computeLegalRepName(
          validated.legal_rep_first_name,
          validated.legal_rep_last_name,
        ),
      };
    } catch (error) {
      throw this.handleError(error, "fetch investor profile");
    }
  }

  async updateProfile(data: HubProfileUpdateRequest): Promise<HubInvestorProfile> {
    try {
      const response = await apiClient.patch<HubInvestorProfile>(`${this.basePath}/profile`, data);

      const validated = this.validateResponse<HubInvestorProfile>(response.data, [
        "uuid",
        "type",
        "display_name",
      ]);

      return {
        ...validated,
        legal_rep_name: this.computeLegalRepName(
          validated.legal_rep_first_name,
          validated.legal_rep_last_name,
        ),
      };
    } catch (error) {
      throw this.handleError(error, "update investor profile");
    }
  }

  async getDashboard(): Promise<HubDashboardSummary> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      type BackendHubInvestment = Omit<HubInvestment, "state"> & {
        state: string;
      };
      type BackendDashboard = Omit<HubDashboardSummary, "recent_investments"> & {
        recent_investments: BackendHubInvestment[];
      };

      const url = this.buildUrlWithInvestor(`${this.basePath}/dashboard`, profileUuid);
      const response = await apiClient.get<BackendDashboard>(url);

      const validated = this.validateResponse<BackendDashboard>(response.data, [
        "total_investments",
        "total_invested_amount",
        "recent_investments",
      ]);

      return {
        ...validated,
        recent_investments: validated.recent_investments.map((inv) =>
          this.transformHubInvestment(inv),
        ),
      };
    } catch (error) {
      throw this.handleError(error, "fetch dashboard summary");
    }
  }

  async getPendingActions(params: HubPendingActionsParams = {}): Promise<HubPendingActions> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      const queryParams = new URLSearchParams();
      if (params.expiring_days !== undefined) {
        queryParams.append("expiring_days", params.expiring_days.toString());
      }
      if (profileUuid) {
        queryParams.append("investor_uuid", profileUuid);
      }

      const queryString = queryParams.toString();
      const url = queryString
        ? `${`${this.basePath}/pending-actions`}?${queryString}`
        : `${this.basePath}/pending-actions`;

      const response = await apiClient.get<HubPendingActions>(url);
      return this.validateResponse<HubPendingActions>(response.data, [
        "contracts_to_sign",
        "pending_payments",
        "expiring_reservations",
        "total_count",
        "has_actions",
      ]);
    } catch (error) {
      throw this.handleError(error, "fetch pending actions");
    }
  }

  async getInvestments(params: HubInvestmentListParams = {}): Promise<HubInvestmentListResponse> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      const queryParams = new URLSearchParams();
      if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
      if (params.offset !== undefined) queryParams.append("offset", params.offset.toString());
      if (profileUuid) queryParams.append("investor_uuid", profileUuid);

      const queryString = queryParams.toString();
      const url = queryString
        ? `${`${this.basePath}/investments`}?${queryString}`
        : `${this.basePath}/investments`;

      type BackendHubInvestment = Omit<HubInvestment, "state"> & {
        state: string;
      };
      type BackendListResponse = Omit<HubInvestmentListResponse, "investments"> & {
        investments: BackendHubInvestment[];
      };

      const response = await apiClient.get<BackendListResponse>(url);
      const validated = this.validateResponse<BackendListResponse>(response.data, [
        "investments",
        "total_count",
        "limit",
        "offset",
      ]);

      return {
        ...validated,
        investments: validated.investments.map((inv) => this.transformHubInvestment(inv)),
      };
    } catch (error) {
      throw this.handleError(error, "fetch investments list");
    }
  }

  async getInvestmentDetail(uuid: string): Promise<HubInvestment> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      type BackendHubInvestment = Omit<HubInvestment, "state"> & {
        state: string;
      };

      const url = this.buildUrlWithInvestor(`${this.basePath}/investments/${uuid}`, profileUuid);
      const response = await apiClient.get<BackendHubInvestment>(url);

      const validated = this.validateResponse<BackendHubInvestment>(response.data, [
        "uuid",
        "amount",
        "state",
        "project_name",
      ]);

      return this.transformHubInvestment(validated);
    } catch (error) {
      throw this.handleError(error, `fetch investment detail ${uuid}`);
    }
  }

  async listInvestmentDocuments(
    investmentUuid: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<InvestmentDocumentResponse>> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.append("limit", params.limit.toString());
      if (params?.offset) searchParams.append("offset", params.offset.toString());

      const basePath = `${this.basePath}/investments/${investmentUuid}/documents`;
      if (profileUuid) searchParams.append("investor_uuid", profileUuid);
      const query = searchParams.toString();
      const url = query ? `${basePath}?${query}` : basePath;

      const response = await apiClient.get<PaginatedResponse<InvestmentDocumentResponse>>(url);

      return this.validateResponse<PaginatedResponse<InvestmentDocumentResponse>>(response.data, [
        "items",
        "total_count",
      ]);
    } catch (error) {
      throw this.handleError(error, `fetch investment documents ${investmentUuid}`);
    }
  }

  async getDocumentDownloadUrl(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      const url = this.buildUrlWithInvestor(
        `${this.basePath}/investments/${investmentUuid}/documents/${documentUuid}/download`,
        profileUuid,
      );
      const response = await apiClient.get<DocumentDownloadResponse>(url);

      return this.validateResponse<DocumentDownloadResponse>(response.data, [
        "signed_url",
        "content_type",
        "filename",
      ]);
    } catch (error) {
      throw this.handleError(error, `fetch document URL ${documentUuid}`);
    }
  }

  async downloadDocument(
    investmentUuid: string,
    documentUuid: string,
    filename?: string,
  ): Promise<void> {
    try {
      const urlResponse = await this.getDocumentDownloadUrl(investmentUuid, documentUuid);

      const fetchResponse = await fetch(urlResponse.signed_url);
      if (!fetchResponse.ok) {
        throw new ApiClientError({
          message: `Failed to download document: ${fetchResponse.status} ${fetchResponse.statusText}`,
          code: "DOWNLOAD_ERROR",
          status: fetchResponse.status,
        });
      }

      const blob = await fetchResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || urlResponse.filename || "document";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      throw this.handleError(error, `download document ${documentUuid}`);
    }
  }

  async getSigningUrl(
    investmentUuid: string,
    documentUuid: string,
    request: HubSigningUrlRequest,
  ): Promise<HubSigningUrlResponse> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      const url = this.buildUrlWithInvestor(
        `${this.basePath}/investments/${investmentUuid}/documents/${documentUuid}/signing-url`,
        profileUuid,
      );
      const response = await apiClient.post<HubSigningUrlResponse>(url, request);

      return response.data;
    } catch (error) {
      throw this.handleError(error, `get signing URL for document ${documentUuid}`);
    }
  }

  async uploadPaymentReceipt(
    investmentUuid: string,
    file: File,
  ): Promise<DocumentWithReviewResponse> {
    try {
      const profileUuid = this.getSelectedProfileUuid() ?? undefined;

      const formData = new FormData();
      formData.append("file", file);

      const url = this.buildUrlWithInvestor(
        `${this.basePath}/investments/${investmentUuid}/payment-receipt`,
        profileUuid,
      );
      const response = await apiClient.post<DocumentWithReviewResponse>(url, formData);

      return this.validateResponse<DocumentWithReviewResponse>(response.data, [
        "document",
        "review",
      ]);
    } catch (error) {
      throw this.handleError(error, `upload payment receipt for investment ${investmentUuid}`);
    }
  }
}
